import React, { useState } from 'react';
import { Button, Form, Input, Modal, Select, Space, Table, Tag, Upload, message } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { epcService } from '@/services/epc.service';
import { resolvePublicAssetUrl } from '@/config/apiBaseUrl';

const STAGES = ['before', 'during', 'after'];

type Props = { projectId?: string };

export const FieldPhotosPanel: React.FC<Props> = ({ projectId }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const packagesQ = useQuery({
    queryKey: ['cep-packages', projectId],
    queryFn: () => epcService.getExecutionPackages(projectId),
    enabled: !!projectId,
    staleTime: 30_000,
  });

  const photosQ = useQuery({
    queryKey: ['cep-photos', projectId],
    queryFn: () => epcService.getCepPhotos(projectId),
    enabled: !!projectId,
    staleTime: 15_000,
  });

  const createMut = useMutation({
    mutationFn: async (values: Record<string, unknown>) => {
      const raw = fileList[0]?.originFileObj as File | undefined;
      if (!raw) throw new Error('Choose an image file to upload');
      return epcService.uploadPhoto({
        file: raw,
        projectId: String(projectId),
        packageId: String(values.packageId),
        stage: values.stage ? String(values.stage) : 'during',
        caption: values.caption ? String(values.caption) : undefined,
      });
    },
    onSuccess: () => {
      message.success('Photo uploaded');
      setOpen(false);
      form.resetFields();
      setFileList([]);
      void qc.invalidateQueries({ queryKey: ['cep-photos'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => epcService.deletePhoto(id),
    onSuccess: () => {
      message.success('Photo removed');
      void qc.invalidateQueries({ queryKey: ['cep-photos'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  if (!projectId) return <Tag>Select a project for field photos</Tag>;

  const rows = (photosQ.data as any[]) || [];
  const packages = (packagesQ.data as any[]) || [];

  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <Button type="primary" onClick={() => setOpen(true)}>
          Upload photo
        </Button>
        <Tag color="geekblue">CEP field photos</Tag>
        <Tag>Stored under /uploads/epc/field-photos/…</Tag>
      </Space>
      <Table
        size="small"
        rowKey={(r) => String(r.id)}
        loading={photosQ.isLoading}
        dataSource={rows}
        columns={[
          { title: 'Stage', dataIndex: 'stage', width: 90 },
          { title: 'Caption', dataIndex: 'caption', ellipsis: true },
          {
            title: 'Preview',
            dataIndex: 'storage_url',
            width: 100,
            render: (v: string) => {
              const src = resolvePublicAssetUrl(v);
              return src ? (
                <a href={src} target="_blank" rel="noreferrer">
                  <img
                    src={src}
                    alt=""
                    style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 4 }}
                  />
                </a>
              ) : (
                '—'
              );
            },
          },
          {
            title: 'Path',
            dataIndex: 'storage_url',
            ellipsis: true,
            render: (v: string) => v || '—',
          },
          {
            title: '',
            width: 90,
            render: (_: unknown, r: any) => (
              <Button
                danger
                size="small"
                onClick={() => deleteMut.mutate(String(r.id))}
                loading={deleteMut.isPending}
              >
                Delete
              </Button>
            ),
          },
        ]}
      />
      <Modal
        title="Upload field photo"
        open={open}
        onCancel={() => {
          setOpen(false);
          setFileList([]);
        }}
        onOk={() => form.submit()}
        confirmLoading={createMut.isPending}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(v) => createMut.mutate(v)}
          initialValues={{ stage: 'during' }}
        >
          <Form.Item name="packageId" label="Execution package" rules={[{ required: true }]}>
            <Select
              options={packages.map((p: any) => ({
                value: String(p.id),
                label: p.package_number,
              }))}
            />
          </Form.Item>
          <Form.Item name="stage" label="Stage" rules={[{ required: true }]}>
            <Select options={STAGES.map((s) => ({ value: s, label: s }))} />
          </Form.Item>
          <Form.Item label="Image" required>
            <Upload
              listType="picture-card"
              maxCount={1}
              accept="image/*"
              fileList={fileList}
              beforeUpload={() => false}
              onChange={({ fileList: next }) => setFileList(next)}
            >
              {fileList.length === 0 ? 'Select' : null}
            </Upload>
          </Form.Item>
          <Form.Item name="caption" label="Caption">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FieldPhotosPanel;
